import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GroupAgeGroup, GroupType, GroupWithMembers } from '@schooly/api';
import { useRenderGroupType } from '@schooly/hooks/use-render-group-type';
import {
  ArchiveIcon,
  CrossIcon,
  EditIcon,
  LockIcon,
  TypographyWithOverflowHint,
} from '@schooly/style';
import React, { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SimpleButton } from '../../../components/uikit/SimpleButton/SimpleButton';
import { ModalPanel, ModalTitle } from '../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../components/uikit-components/Modal/ModalHeader';
import { AgeGroupLabel } from '../AgeGroupLabel';
import { GroupPreviewModalCloneButton } from './GroupPreviewModalCloneButton';

interface EditableProps {
  onEditClick: (property?: keyof GroupWithMembers) => void;
}

export interface ViewGroupModalInfoProps extends EditableProps {
  group?: GroupWithMembers;
  validity?: string;
  ageGroups: GroupAgeGroup[];
  onModalClose: VoidFunction;
  locked?: boolean;
  active?: boolean;
  showCloneButton?: boolean;
  withGoBack?: boolean;
  titleErrorText?: string;
}
//TODO Move ellipsis function to common utils
export function ellipsis(s?: string, threshold = 40) {
  return s && s.length > threshold ? s.substring(0, threshold) + '...' : s;
}

export const Editable: FC<
  PropsWithChildren<
    EditableProps & { prop: keyof GroupWithMembers; hasValue?: boolean; locked?: boolean }
  >
> = ({ hasValue, prop, onEditClick, locked, children }) => {
  const onClick = useCallback(() => {
    !locked && onEditClick(prop);
  }, [onEditClick, prop, locked]);

  return (
    <>
      <SimpleButton
        sx={{
          pointerEvents: locked ? 'none' : 'all',
          maxWidth: '100%',
          '& > .MuiBox-root': { minWidth: 0 },
        }}
        endIcon={
          locked ? (
            <Box sx={{ opacity: 0.5, mt: -0.5 }}>
              <LockIcon />
            </Box>
          ) : (
            <Box sx={{ ml: hasValue ? 0 : -1 }}>
              <EditIcon />
            </Box>
          )
        }
        onClick={onClick}
        iconsOnHover={hasValue && !locked}
      >
        {children}
      </SimpleButton>
    </>
  );
};

export const GroupPreviewModalInfo: React.FC<ViewGroupModalInfoProps> = ({
  group,
  validity,
  ageGroups,
  onModalClose,
  onEditClick,
  locked,
  active,
  showCloneButton,
  withGoBack,
  titleErrorText,
}) => {
  const { formatMessage } = useIntl();
  const renderGroupType = useRenderGroupType();
  const sortedAgeGroups = useMemo(
    () => [...ageGroups].sort((a, b) => a.order - b.order),
    [ageGroups],
  );

  return (
    <Box>
      <ModalHeader
        active={active}
        title={
          <Editable locked={locked} hasValue={!!group?.name} onEditClick={onEditClick} prop="name">
            <ModalTitle>{ellipsis(group?.name ?? '', 25)}</ModalTitle>
          </Editable>
        }
        errorText={titleErrorText}
        withGoBack={withGoBack}
      >
        {showCloneButton && <GroupPreviewModalCloneButton group={group} />}
        <IconButton data-test-id="modal-close" onClick={onModalClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>

      <ModalPanel active={active} withBorderBottom>
        <Stack direction="row" gap={2}>
          <Box sx={{ minWidth: 0, flex: '2 1 0' }}>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="groups-Validity" />
            </Typography>
            <Editable
              locked={locked}
              hasValue={!!validity}
              onEditClick={onEditClick}
              prop="validity"
            >
              <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                {validity}
              </TypographyWithOverflowHint>
            </Editable>
          </Box>

          <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage
                id={(() => {
                  switch (group?.group_type) {
                    case GroupType.TutorGroup:
                      return 'groups-Type';
                    case GroupType.Subject:
                      return 'groups-GroupSubject';
                    case GroupType.NoSubject:
                      return 'groups-NoSubject';
                    default:
                      return 'groups-TypeOrSubject';
                  }
                })()}
              />
            </Typography>
            <Editable
              locked={locked}
              hasValue
              onEditClick={onEditClick}
              prop={group?.group_type === GroupType.Subject ? 'subject' : 'group_type'}
            >
              <Stack direction="row" gap={0.75}>
                {group?.subject?.archived ? (
                  <Tooltip title={formatMessage({ id: 'schoolProperty-Archived-subject' })}>
                    <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                      <ArchiveIcon /> {renderGroupType(group) ?? '—'}
                    </TypographyWithOverflowHint>
                  </Tooltip>
                ) : (
                  <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                    {renderGroupType(group) ?? '—'}
                  </TypographyWithOverflowHint>
                )}
              </Stack>
            </Editable>
          </Box>

          <Box sx={{ minWidth: 0, flex: '1 1 0' }}>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="groups-GroupAgeGroup" />
            </Typography>

            <Editable
              locked={locked}
              hasValue={!!sortedAgeGroups.length}
              onEditClick={onEditClick}
              prop="age_groups"
            >
              <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                {sortedAgeGroups.length
                  ? sortedAgeGroups.map((ag, i) => (
                      <React.Fragment key={ag.id}>
                        {i > 0 && ', '}
                        <AgeGroupLabel ageGroup={ag} variant="inherit" />
                      </React.Fragment>
                    ))
                  : ''}
              </TypographyWithOverflowHint>
            </Editable>
          </Box>

          <Box sx={{ minWidth: 0, flex: '2 1 0' }}>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="groups-GroupDescription" />
            </Typography>

            <Editable hasValue={!!group?.description} onEditClick={onEditClick} prop="description">
              <TypographyWithOverflowHint variant="h3" color="text.primary" noWrap>
                {group?.description ?? ''}
              </TypographyWithOverflowHint>
            </Editable>
          </Box>
        </Stack>
      </ModalPanel>
    </Box>
  );
};
